import { createContext, FunctionComponent, PropsWithChildren } from 'react';
import { useContext, useEffect, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { MediaItemV2 } from '../../../app-kit/components/Image/types';
import { MediaCountIconsMd } from '../../components/MediaCountButtons/types';
import {
  MediaGalleryCarouselProvider,
  useMediaGalleryCarouselContext,
} from '../../components/MediaGalleryCarousel/MediaGalleryCarouselProvider';
import { ResultOf } from 'gql.tada';
import { propertyPagePropertyPageFragment } from '../../graphql/propertyPageQuery';
import {
  ImageEventData,
  trackFullScreenCarouselView,
} from '../../../analytics/event-tracking/trackFullScreenCarouselView';
import { mapApiWebLinkToWebLinkProps } from '../../../app-kit/components/WebLink/helpers/mapApiWebLinkToWebLinkProps';

type PropertyPageBySlugData = NonNullable<ResultOf<typeof propertyPagePropertyPageFragment>>;

export interface MediaCount {
  iconNameMd: MediaCountIconsMd;
  title: string;
  value: string;
  imageIndexFrom?: number | null;
}

export interface MediaGalleryLightBoxContext {
  mediaItems: MediaItemV2[];
  mediaCounts: MediaCount[];
  currentSlideIndex: number;
  totalSlidesCount: number;
  setCurrentSlideByIndex: (targetIndex: number, disableAnimation: boolean) => void;
  enableAnimation: () => void;
  propertyPageBySlugData: PropertyPageBySlugData | null;
}

export const MediaGalleryLightBoxContext = createContext<MediaGalleryLightBoxContext | undefined>(
  undefined,
);

export const useMediaGalleryLightBoxContext = () => {
  const context = useContext(MediaGalleryLightBoxContext);
  if (context === undefined) {
    throw new Error(
      'MediaGalleryLightBoxContext must be used within a MediaGalleryLightBoxContext.Provider',
    );
  }
  return context;
};

export const mergeImageTrackingEventDataWithUserAction = (
  eventData: ImageEventData,
  userAction: string,
): ImageEventData => {
  return {
    ...eventData,
    data: {
      ...eventData.data,
      user_action: userAction,
    },
  };
};

export const useFullScreenCarouselTracking = (
  openAction: string,
  mediaItems: MediaItemV2[],
  track: (eventData: ImageEventData) => void = trackFullScreenCarouselView,
) => {
  const [shouldTrackImageChange, setShouldTrackImageChange] = useState(false);
  const { currentSlideIndex } = useMediaGalleryCarouselContext();

  useEffect(() => {
    requestAnimationFrame(() => {
      track(
        mergeImageTrackingEventDataWithUserAction(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: Object is possibly 'undefined'
          mediaItems[currentSlideIndex].tracking.fullScreenCarouselView,
          openAction,
        ),
      );
      setShouldTrackImageChange(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldTrackImageChange) {
      track(
        mergeImageTrackingEventDataWithUserAction(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: Object is possibly 'undefined'
          mediaItems[currentSlideIndex].tracking.fullScreenCarouselView,
          'MediaGallery:ImageChange',
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideIndex]);
};

interface InnerContextProps extends Pick<MediaGalleryLightBoxProviderProps, 'openAction'> {
  mediaCounts: MediaCount[];
  mediaItems: MediaItemV2[];
  propertyPageBySlugData: PropertyPageBySlugData | null;
}

const InnerContext: FunctionComponent<PropsWithChildren<InnerContextProps>> = ({
  children,
  openAction,
  mediaItems,
  mediaCounts,
  propertyPageBySlugData,
}) => {
  const { currentSlideIndex, totalSlidesCount, setCurrentSlideByIndex, enableAnimation } =
    useMediaGalleryCarouselContext();

  useFullScreenCarouselTracking(openAction, mediaItems);

  return (
    <MediaGalleryLightBoxContext.Provider
      value={{
        mediaCounts,
        mediaItems,
        currentSlideIndex,
        totalSlidesCount,
        setCurrentSlideByIndex,
        enableAnimation,
        propertyPageBySlugData,
      }}
    >
      {children}
    </MediaGalleryLightBoxContext.Provider>
  );
};

interface GetInitialSlideIndex {
  gallery: PropertyPageBySlugData['mediaGalleryV2'];
  initialImageIndex?: number;
  galleryItemKey?: string;
}

export const getInitialSlideIndex = ({
  gallery,
  galleryItemKey,
  initialImageIndex,
}: GetInitialSlideIndex): number => {
  let currentSlide = 0;
  if (galleryItemKey && gallery) {
    const itemIndexWithKey = gallery.items.findIndex((i) => i.key === galleryItemKey);
    if (itemIndexWithKey !== -1) {
      currentSlide = itemIndexWithKey;
    }
  } else if (initialImageIndex) {
    currentSlide = initialImageIndex;
  }

  return currentSlide;
};

const MediaGalleryCarouselProviderGlobalStyle = createGlobalStyle`
  .media-gallery-carousel {
    height: 100%;
  }
`;

interface MediaGalleryLightBoxProviderProps {
  openAction: string;
  initialImageIndex?: number;
  galleryItemKey?: string;
}

export const MediaGalleryLightBoxProvider: FunctionComponent<
  PropsWithChildren<MediaGalleryLightBoxProviderProps>
> = ({ children, openAction, initialImageIndex, galleryItemKey }) => {
  const propertyPageBySlugData = usePropertyPageQuery()?.data?.propertyPageBySlugV3;
  const gallery = propertyPageBySlugData?.mediaGalleryV2;
  if (!gallery) return null;

  const currentSlide = getInitialSlideIndex({ gallery, galleryItemKey, initialImageIndex });
  const { items, mediaCounts } = gallery;
  const itemsWithWebLinkProps = items.map((item) => ({
    ...item,
    searchByImageButton: item.searchByImageButton
      ? mapApiWebLinkToWebLinkProps(item.searchByImageButton)
      : null,
  }));
  return (
    <>
      <MediaGalleryCarouselProviderGlobalStyle />
      <MediaGalleryCarouselProvider
        naturalSlideWidth={600} // Required value, but actually ignored as we're full screen modal
        naturalSlideHeight={450} // Required value, but actually ignored as we're full screen modal
        totalSlides={items.length}
        visibleSlides={1}
        lockOnWindowScroll
        isIntrinsicHeight
        dragStep={1}
        dragEnabled={false}
        currentSlide={currentSlide}
        className="media-gallery-carousel"
      >
        <InnerContext
          mediaCounts={mediaCounts}
          mediaItems={itemsWithWebLinkProps}
          openAction={openAction}
          propertyPageBySlugData={propertyPageBySlugData}
        >
          {children}
        </InnerContext>
      </MediaGalleryCarouselProvider>
    </>
  );
};
